// Enhanced equity calculation engine with configurable weights
import { supabase } from './supabase'
import { 
  EquityWeightConfig, 
  ContributionScore, 
  EquityAllocation, 
  EquityCalculationResult,
  TaskWeight 
} from '@/types/equity-calculator'

// Default configuration
export const DEFAULT_EQUITY_CONFIG: EquityWeightConfig = {
  effort_weights: {
    S: 1,
    M: 3,
    L: 8,
    XL: 13,
  },
  capital_weights: {
    S: 1,
    M: 5,
    L: 10,
    XL: 20,
  },
  frequency_multipliers: {
    'One-time': 1,
    'Daily': 20,
    'Weekly': 4,
    'Monthly': 2,
    'Quarterly': 1.5,
    'Ongoing': 10,
  },
  founder_bonus: 1.2, // 20% bonus
  dilution_protection: 0.8, // Founders retain 80% of their allocation post-dilution
}

export class EquityCalculationEngine {
  private config: EquityWeightConfig
  private projectId: string

  constructor(projectId: string, config?: Partial<EquityWeightConfig>) {
    this.projectId = projectId
    this.config = { ...DEFAULT_EQUITY_CONFIG, ...config }
  }

  async calculateEquityAllocations(
    totalShares: number = 10000000,
    optionPoolPercentage: number = 15,
    accountForDilution: boolean = true
  ): Promise<EquityCalculationResult> {
    try {
      // Fetch all necessary data
      const [tasks, contributions, fundingRounds, founderRole, investmentData] = await Promise.all([
        this.fetchTasks(),
        this.fetchContributions(),
        this.fetchFundingRounds(),
        this.fetchFounderRole(),
        this.fetchInvestments(),
      ])

      // Calculate task weights
      const taskWeights = this.calculateTaskWeights(tasks)

      // Calculate contribution scores
      const userScores = this.calculateContributionScores(
        contributions,
        taskWeights,
        founderRole?.id
      )

      // Calculate ownership distribution
      const ownershipDistribution = await this.calculateOwnershipDistribution(
        fundingRounds,
        investmentData,
        accountForDilution
      )

      // Convert scores to allocations
      const allocations = this.convertScoresToAllocations(
        userScores,
        contributions,
        totalShares,
        ownershipDistribution,
        optionPoolPercentage
      )

      // Create result object
      const result: EquityCalculationResult = {
        allocations,
        total_shares: totalShares,
        total_score: Object.values(userScores).reduce((sum, score) => sum + score.total_score, 0),
        founder_ownership: ownershipDistribution.founderOwnership,
        investor_ownership: ownershipDistribution.investorOwnership,
        option_pool: optionPoolPercentage,
        calculated_at: new Date().toISOString(),
        config_used: this.config,
      }

      return result
    } catch (error) {
      console.error('Error calculating equity allocations:', error)
      throw error
    }
  }

  private async fetchTasks() {
    const { data, error } = await supabase
      .from('tasks')
      .select('*')
      .eq('project_id', this.projectId)

    if (error) throw error
    return data || []
  }

  private async fetchContributions() {
    const { data, error } = await supabase
      .from('contributions')
      .select(`
        *,
        users (id, first_name, last_name, email, role_id),
        tasks (id)
      `)
      .eq('project_id', this.projectId)

    if (error) throw error
    return data || []
  }

  private async fetchFundingRounds() {
    const { data, error } = await supabase
      .from('funding_rounds')
      .select('*')
      .eq('project_id', this.projectId)
      .order('round_date', { ascending: true })

    if (error) throw error
    return data || []
  }

  private async fetchFounderRole() {
    const { data, error } = await supabase
      .from('roles')
      .select('id')
      .eq('name', 'founder')
      .single()

    return data
  }

  private async fetchInvestments() {
    const { data, error } = await supabase
      .from('share_ledger')
      .select(`
        shares,
        transaction_type,
        users (id, first_name, last_name)
      `)
      .eq('project_id', this.projectId)
      .eq('transaction_type', 'share_purchase')

    if (error) throw error
    return data || []
  }

  private calculateTaskWeights(tasks: any[]): Record<string, TaskWeight> {
    const taskWeights: Record<string, TaskWeight> = {}

    tasks.forEach(task => {
      taskWeights[task.id] = {
        id: task.id,
        effort_weight: this.config.effort_weights[task.tshirt_size_effort as keyof typeof this.config.effort_weights] || 1,
        capital_weight: this.config.capital_weights[task.tshirt_size_capital as keyof typeof this.config.capital_weights] || 1,
        frequency_multiplier: this.config.frequency_multipliers[task.frequency as keyof typeof this.config.frequency_multipliers] || 1,
        founder_multiplier: task.founder_leverage ? this.config.founder_bonus : 1,
      }
    })

    return taskWeights
  }

  private calculateContributionScores(
    contributions: any[],
    taskWeights: Record<string, TaskWeight>,
    founderRoleId?: string
  ): Record<string, ContributionScore> {
    const userScores: Record<string, ContributionScore> = {}

    contributions.forEach(contrib => {
      const userId = contrib.user_id
      const taskWeight = taskWeights[contrib.task_id]

      if (!taskWeight) return

      if (!userScores[userId]) {
        userScores[userId] = {
          user_id: userId,
          total_score: 0,
          effort_score: 0,
          capital_score: 0,
          task_count: 0,
          founder_status: contrib.users?.role_id === founderRoleId,
        }
      }

      // Calculate weighted scores
      const effortScore = (contrib.effort_value || 0) * taskWeight.effort_weight * taskWeight.frequency_multiplier
      const capitalScore = (contrib.capital_value || 0) * taskWeight.capital_weight * 0.001 // Scale down capital

      // Apply founder multiplier if user is a founder
      const multiplier = userScores[userId].founder_status ? taskWeight.founder_multiplier : 1

      userScores[userId].effort_score += effortScore * multiplier
      userScores[userId].capital_score += capitalScore * multiplier
      userScores[userId].total_score += (effortScore + capitalScore) * multiplier
      userScores[userId].task_count += 1
    })

    return userScores
  }

  private async calculateOwnershipDistribution(
    fundingRounds: any[],
    investments: any[],
    accountForDilution: boolean
  ) {
    let founderOwnership = 100
    let investorOwnership = 0

    if (accountForDilution && fundingRounds.length > 0) {
      // Calculate cumulative dilution from funding rounds
      fundingRounds.forEach(round => {
        if (!round.post_money_valuation || !round.pre_money_valuation) return
        const investment = round.post_money_valuation - round.pre_money_valuation
        const dilutionPercentage = (investment / round.post_money_valuation) * 100
        
        // Apply dilution protection for founders
        const actualDilution = dilutionPercentage * (1 - this.config.dilution_protection)
        founderOwnership -= actualDilution
        investorOwnership += dilutionPercentage
      })
    }

    return {
      founderOwnership: Math.max(founderOwnership, 0),
      investorOwnership: Math.min(investorOwnership, 100),
    }
  }

  private convertScoresToAllocations(
    userScores: Record<string, ContributionScore>,
    contributions: any[],
    totalShares: number,
    ownershipDistribution: { founderOwnership: number; investorOwnership: number },
    optionPoolPercentage: number
  ): EquityAllocation[] {
    const allocations: EquityAllocation[] = []
    const totalScore = Object.values(userScores).reduce((sum, score) => sum + score.total_score, 0)

    // Calculate available shares for founders/employees
    const founderShares = Math.round((ownershipDistribution.founderOwnership / 100) * totalShares)
    const optionPoolShares = Math.round((optionPoolPercentage / 100) * totalShares)
    const availableShares = founderShares - optionPoolShares

    for (const [userId, score] of Object.entries(userScores)) {
      // Find user info
      const userContrib = contributions.find(c => c.user_id === userId)
      const userName = userContrib?.users
        ? `${userContrib.users.first_name || ''} ${userContrib.users.last_name || ''}`.trim() || userContrib.users.email
        : 'Unknown User'

      // Calculate shares based on contribution score
      const sharePercentageOfAvailable = totalScore > 0 ? (score.total_score / totalScore) : 0
      const shares = Math.round(sharePercentageOfAvailable * availableShares)
      const equityPercentage = (shares / totalShares) * 100

      allocations.push({
        user_id: userId,
        user_name: userName,
        contribution_score: score.total_score,
        equity_percentage: equityPercentage,
        shares: shares,
      })
    }

    // Sort by equity percentage descending
    allocations.sort((a, b) => b.equity_percentage - a.equity_percentage)

    return allocations
  }

  async saveCalculationHistory(result: EquityCalculationResult, triggeredBy: string, notes?: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('equity_calculation_history')
        .insert({
          project_id: this.projectId,
          calculated_at: result.calculated_at,
          result: result as unknown as import('@/database.types').Json,
          triggered_by: triggeredBy,
          notes: notes,
        })

      if (error) throw error
    } catch (error) {
      console.error('Error saving calculation history:', error)
      throw error
    }
  }

  async updateCapTable(allocations: EquityAllocation[]): Promise<void> {
    try {
      // Get common stock equity class
      const { data: commonStock } = await supabase
        .from('equity_classes')
        .select('id')
        .eq('class_name', 'Common')
        .single()

      if (!commonStock) throw new Error('Common stock class not found')

      // Use transaction to update cap table
      const { error: deleteError } = await supabase
        .from('cap_table_entries')
        .delete()
        .eq('project_id', this.projectId)
        .eq('equity_class_id', commonStock.id)

      if (deleteError) throw deleteError

      // Insert new allocations
      const entries = allocations.map(allocation => ({
        user_id: allocation.user_id,
        equity_class_id: commonStock.id,
        shares: allocation.shares,
        percentage: allocation.equity_percentage,
        project_id: this.projectId,
      }))

      const { error: insertError } = await supabase
        .from('cap_table_entries')
        .insert(entries)

      if (insertError) throw insertError
    } catch (error) {
      console.error('Error updating cap table:', error)
      throw error
    }
  }

  // Configuration management
  updateConfig(newConfig: Partial<EquityWeightConfig>): void {
    this.config = { ...this.config, ...newConfig }
  }

  getConfig(): EquityWeightConfig {
    return { ...this.config }
  }

  resetConfig(): void {
    this.config = { ...DEFAULT_EQUITY_CONFIG }
  }
}